import { AuthContext, Language } from '@core/shared';
import { EmailTemplate, EmailTemplateType, OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, ErrorMessagePage, Heading, Input, LoaderPage, Select, Switch, TextArea } from '@core/ui';
import { Navigate, useNavigate, useParams } from '@online-webinar/router';
import { useMutation, useQuery } from '@tanstack/react-query';
import i18n from 'i18next';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewPanelOrganism } from '../../organism';

export const EditEmailTemplatePage = () => {
    const navigate = useNavigate();
    const [language, setLanguage] = useState<Language>(i18n.language as Language);
    const { t } = useTranslation();
    const { templateType, subTemplate }: { templateType: string; subTemplate: string } = useParams({ strict: false });

    const [body, setBody] = useState('');
    const [subject, setSubject] = useState('');
    const [includeCalendarAttachment, setIncludeCalendarAttachment] = useState(false);

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewBody, setPreviewBody] = useState('');
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

    const canManageEmailTemplates = useContext(AuthContext).canManageEmailTemplates;
    if (!canManageEmailTemplates) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const translateEmailType = (): EmailTemplateType => {
        if (parseInt(templateType) === +templateType) {
            return parseInt(templateType);
        }

        return EmailTemplateType[templateType as keyof typeof EmailTemplateType];
    };

    const isCalendarTemplate = () => {
        if (!templateType) return false;
        const type = translateEmailType();
        return type === EmailTemplateType.WebinarInterest || type === EmailTemplateType.WebinarReminder || type === EmailTemplateType.WebinarInvite;
    };

    const {
        isLoading,
        data: emailTemplate,
        error,
        refetch: refetchTemplate
    } = useQuery(
        OptionsApi.emailTemplate.get({ language, templateType: translateEmailType(), subTemplate }, { refetchOnWindowFocus: false, enabled: !!templateType })
    );

    const { data: calendarAttachment } = useQuery(
        OptionsApi.emailTemplate.getCalendarAttachment(
            { templateType: translateEmailType(), subTemplate },
            { refetchOnWindowFocus: false, enabled: !!templateType && isCalendarTemplate() }
        )
    );

    const previewTemplateMutation = useMutation(OptionsApi.emailTemplate.preview());
    const setTemplateMutation = useMutation(OptionsApi.emailTemplate.set());
    const setCalendarAttachmentMutation = useMutation(OptionsApi.emailTemplate.setCalendarAttachment());

    const saveTemplate = () => {
        setTemplateMutation.mutate(
            { language, templateType: translateEmailType(), subTemplate, body, subject },
            {
                onSuccess: () => refetchTemplate()
            }
        );
    };

    const mutationError = previewTemplateMutation.error || setTemplateMutation.error;

    useEffect(() => {
        if (emailTemplate) {
            setBody(emailTemplate.body);
            setSubject(emailTemplate.subject);
        }
    }, [emailTemplate]);

    useEffect(() => {
        if (calendarAttachment !== undefined) {
            setIncludeCalendarAttachment(calendarAttachment);
        }
    }, [calendarAttachment]);

    const handleCalendarAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIncludeCalendarAttachment(checked);
        setCalendarAttachmentMutation.mutate({ templateType: translateEmailType(), subTemplate, include: checked });
    };

    useEffect(() => {
        if (!body || !subject) {
            setPreviewLoading(false);
            setPreviewSubject('');
            setPreviewBody('');
            return;
        }

        if (previewTemplateMutation.isPending) {
            return;
        }

        if (previewTemplate?.body === body && previewTemplate?.subject === subject) {
            return;
        }

        setPreviewLoading(true);
        const handle = setTimeout(() => {
            previewTemplateMutation.mutate(
                { templateType: translateEmailType(), body, subject },
                {
                    onSuccess: (data) => {
                        setPreviewLoading(false);
                        setPreviewTemplate({ body, subject });
                        if (data) {
                            setPreviewSubject(data.subject);
                            setPreviewBody(data.body);
                        }
                    },
                    onError: () => {
                        setPreviewLoading(false);
                        setPreviewTemplate({ body, subject });
                        setPreviewSubject('');
                        setPreviewBody('');
                    }
                }
            );
        }, 500);
        return () => clearTimeout(handle);
    }, [body, subject, previewTemplate, previewTemplateMutation.isPending]);

    if (!templateType) {
        return <Navigate to="/manage/email-template/$templateType" params={{ templateType: 'AccountVerification' }} />;
    }

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading) {
        return <LoaderPage />;
    }

    return (
        <>
            <main className="flex flex-col md:flex-row flex-1">
                <div className="bg-white flex-1 px-4 pb-4 pt-12 md:pt-24">
                    <div className="sticky top-4 max-w-140 mx-auto">
                        <Heading variant="h1" classNames="mt-2 mb-6">
                            {t('Email.Template.PageHeading')}
                        </Heading>
                        <Select
                            label={t('Email.Template.Label.SelectTemplate')}
                            value={templateType}
                            onChange={(e) => navigate({ to: '/manage/email-template/$templateType', params: { templateType: e.target.value } })}
                        >
                            {Object.keys(EmailTemplateType)
                                .filter((key) => !+key)
                                .map((key) => (
                                    <option key={key} value={key}>
                                        {key}
                                    </option>
                                ))}
                        </Select>
                        {mutationError && <ErrorMessage apiError={mutationError} />}
                        <form onSubmit={(e) => e.preventDefault()}>
                            <Input label={t('Email.Template.Label.Subject')} value={subject} onChange={(e) => setSubject(e.target.value)}></Input>
                            <TextArea
                                label={t('Email.Template.Label.Body')}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                textAreaClassNames="min-h-[40vh] field-sizing-content"
                            ></TextArea>
                            {isCalendarTemplate() && (
                                <Switch
                                    classNames="mt-4"
                                    label={t('Email.Template.Label.IncludeCalendarAttachment')}
                                    checked={includeCalendarAttachment}
                                    onChange={handleCalendarAttachmentChange}
                                />
                            )}
                            <Button type="submit" variant="primary" classNames="mt-4" onClick={saveTemplate}>
                                {t('Generic.Button.Save')}
                            </Button>
                        </form>
                    </div>
                </div>
                <div className="bg-info bg-center bg-cover bg-no-repeat flex-1 flex flex-col items-center px-4 pt-4 md:pt-16 xl:pt-12 pb-6">
                    <PreviewPanelOrganism isLoading={previewLoading} subject={previewSubject} html={previewBody} />
                </div>
            </main>
        </>
    );
};
